// components/artifacts/renderers/StreamingArtifactRenderer.tsx
import React, { useMemo } from "react";
import { Loader2 } from "lucide-react";
import { ArtifactLoadingPlaceholder } from "@/types/Artifact";
import { CodeArtifactRenderer } from "./CodeArtifactRenderer";
import { TableArtifactRenderer } from "./TableArtifactRenderer";
import { GraphArtifactRenderer } from "./GraphArtifactRenderer";
import { SqlArtifactRenderer } from "./SqlArtifactRenderer";

interface StreamingArtifactRendererProps {
  loadingItem: ArtifactLoadingPlaceholder;
}

export const StreamingArtifactRenderer: React.FC<StreamingArtifactRendererProps> = ({ 
  loadingItem 
}) => {
  const partialData = loadingItem.partialContent;
  
  // Move all useMemo hooks to the top level
  const tableData = useMemo(() => {
    if (!partialData?.content || loadingItem.type !== "table") return null;
    
    try {
      const parsed = JSON.parse(partialData.content);
      if (parsed.headers && Array.isArray(parsed.headers)) {
        return parsed;
      }
    } catch {
      // Not valid JSON yet
    }
    return null;
  }, [partialData?.content, loadingItem.type]);

  const graphData = useMemo(() => {
    if (!partialData?.content || loadingItem.type !== "graph") return null;
    
    try {
      const parsed = JSON.parse(partialData.content);
      if (parsed.type && parsed.data) {
        return parsed;
      }
    } catch {
      // Not valid JSON yet
    }
    return null;
  }, [partialData?.content, loadingItem.type]);
  
  // Show initial loading state only if no partial content yet
  if (!partialData || !partialData.content) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            Generating {loadingItem.title}...
          </p>
        </div>
      </div>
    );
  }

  const { content: partialContent, language } = partialData;

  // Handle different artifact types during streaming
  switch (loadingItem.type) {
    case "code": {
      // Show code immediately with streaming indicator
      const mockArtifact = {
        ...loadingItem,
        content: partialContent,
        language: language || loadingItem.language || "text",
        isLoading: false, // Temporarily set to render properly
      } as any;
      
      return (
        <div className="relative h-full">
          <div className="absolute right-2 top-2 z-10">
            <div className="flex items-center gap-2 rounded bg-blue-100/80 backdrop-blur px-2 py-1 text-xs text-blue-800 dark:bg-blue-900/80 dark:text-blue-200">
              <Loader2 className="h-3 w-3 animate-spin" />
              Streaming...
            </div>
          </div>
          <CodeArtifactRenderer artifact={mockArtifact} />
        </div>
      );
    }

    case "sql": {
      // Similar to code, show immediately
      const mockArtifact = {
        ...loadingItem,
        content: partialContent,
        language: "sql",
        isLoading: false,
      } as any;
      
      return (
        <div className="relative h-full">
          <div className="absolute right-2 top-2 z-10">
            <div className="flex items-center gap-2 rounded bg-blue-100/80 backdrop-blur px-2 py-1 text-xs text-blue-800 dark:bg-blue-900/80 dark:text-blue-200">
              <Loader2 className="h-3 w-3 animate-spin" />
              Streaming...
            </div>
          </div>
          <SqlArtifactRenderer artifact={mockArtifact} />
        </div>
      );
    }

    case "table": {
      // Use the pre-computed tableData
      if (tableData) {
        const mockArtifact = {
          ...loadingItem,
          content: tableData,
          isLoading: false,
        } as any;
        
        return (
          <div className="relative h-full">
            <div className="absolute right-2 top-2 z-10">
              <div className="flex items-center gap-2 rounded bg-blue-100/80 backdrop-blur px-2 py-1 text-xs text-blue-800 dark:bg-blue-900/80 dark:text-blue-200">
                <Loader2 className="h-3 w-3 animate-spin" />
                Streaming...
              </div>
            </div>
            <TableArtifactRenderer artifact={mockArtifact} />
          </div>
        );
      }

      // Show formatted JSON preview
      return (
        <div className="p-4 h-full overflow-auto">
          <div className="mb-4 flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Building table structure...
          </div>
          <div className="rounded-lg bg-gray-50 p-4 font-mono text-sm dark:bg-gray-800">
            <pre className="whitespace-pre-wrap break-words text-gray-700 dark:text-gray-300">
              {partialContent}
            </pre>
          </div>
        </div>
      );
    }

    case "graph": {
      // Use the pre-computed graphData
      if (graphData) {
        const mockArtifact = {
          ...loadingItem,
          content: graphData,
          isLoading: false,
        } as any;
        
        return (
          <div className="relative h-full">
            <div className="absolute right-2 top-2 z-10">
              <div className="flex items-center gap-2 rounded bg-blue-100/80 backdrop-blur px-2 py-1 text-xs text-blue-800 dark:bg-blue-900/80 dark:text-blue-200">
                <Loader2 className="h-3 w-3 animate-spin" />
                Rendering chart...
              </div>
            </div>
            <GraphArtifactRenderer artifact={mockArtifact} />
          </div>
        );
      }

      // Show formatted preview with chart icon
      return (
        <div className="p-4 h-full overflow-auto">
          <div className="mb-4 flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Preparing chart data...
          </div>
          <div className="rounded-lg bg-gray-50 p-4 font-mono text-sm dark:bg-gray-800">
            <pre className="whitespace-pre-wrap break-words text-gray-700 dark:text-gray-300">
              {partialContent}
            </pre>
          </div>
        </div>
      );
    }

    default:
      // Default streaming view with better styling
      return (
        <div className="p-4 h-full overflow-auto">
          <div className="mb-4 flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Generating {loadingItem.type}...
          </div>
          <div className="rounded-lg bg-gray-50 p-4 font-mono text-sm dark:bg-gray-800">
            <pre className="whitespace-pre-wrap break-words text-gray-700 dark:text-gray-300">
              {partialContent}
            </pre>
          </div>
        </div>
      );
  }
};